// =============================================================================
// TASK-9587 — "Draft it, review and save in DispatchDeck", first slice: a job
// Claude asked for (create_job -> pending_approval) opens in the Job Builder
// pre-filled, with what Claude ASSUMED marked in amber. Pure functions, no DOM,
// no Firestore, so tests/task9587-claude-job-draft.unit.cjs runs them in node.
//
// The link is a record link, `#/dispatch/claude-job/<proposalId>` (TASK-9658
// utils/recordLinks.js parks it across a sign-in): an opaque id and nothing
// else. The server (mcpJobDraft callable) decides the tenant from the signed-in
// session and re-checks who may open it. Everything the request carries (title,
// customer, notes, stops) was typed into a chat and may be someone else's text:
// it is set into form fields with .value and escaped anywhere it is rendered.
// =============================================================================

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Proposal ids are Firestore auto-ids. Anything else is not ours.
const PROPOSAL_ID_RE = /^[A-Za-z0-9]{1,64}$/;

/** A parked record focus ({ type, id }) -> the request id, or null. */
export function claudeJobIdFromFocus(focus) {
  return focus && focus.type === 'claudeJob' && PROPOSAL_ID_RE.test(String(focus.id || '')) ? focus.id : null;
}

const FIELDS = ['customer', 'drivers', 'helpers', 'trucks', 'trailers', 'missing'];
const isYmd = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
const str = (v, max) => String(v == null ? '' : v).slice(0, max);

/**
 * The server's `open` reply -> what the job form gets. Lengths are capped
 * again here (the form's own limits), dates must be YYYY-MM-DD, and only
 * known fields survive. Returns null for anything that is not an open draft.
 */
export function claudeDraftFormFacts(reply) {
  if (!reply || reply.state !== 'open' || !reply.draft || typeof reply.proposalId !== 'string') return null;
  const d = reply.draft;
  const ids = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x).slice(0, 50) : []);
  const startDate = isYmd(d.startDate) ? d.startDate : null;
  const endDate = isYmd(d.endDate) && startDate && d.endDate >= startDate ? d.endDate : startDate;
  const assumed = (Array.isArray(reply.assumed) ? reply.assumed : []).slice(0, 100).map((a) => ({
    field: FIELDS.includes(a && a.field) && a.field !== 'missing' ? a.field : null,
    id: a && typeof a.id === 'string' && a.id ? a.id : null,
    said: str(a && a.said, 80),
    used: str(a && a.used, 400),
  }));
  return {
    proposalId: reply.proposalId,
    jobId: typeof reply.jobId === 'string' ? reply.jobId : null,
    title: str(d.title, 100),
    customerId: typeof d.customerId === 'string' ? d.customerId : '',
    customerName: str(d.customerName, 120),
    startDate,
    endDate,
    multiDay: !!(startDate && endDate && endDate !== startDate),
    location: str(d.location, 200),
    wellName: str(d.wellName, 100),
    notes: str(d.notes, 1000),
    poNumber: str(d.poNumber, 60),
    // TASK-9737 — the request's start time (24-hour HH:MM), or '' = all day.
    startTime: /^([01]\d|2[0-3]):[0-5]\d$/.test(String(d.startTime || '')) ? String(d.startTime) : '',
    waypoints: (Array.isArray(d.waypoints) ? d.waypoints : []).slice(0, 10).map((w) => ({
      name: str(w && w.name, 100),
      url: /^https:\/\/www\.google\.com\/maps/.test(String((w && w.url) || '')) ? str(w.url, 500) : '',
      lat: w && Number.isFinite(w.lat) ? w.lat : null,
      lng: w && Number.isFinite(w.lng) ? w.lng : null,
    })).filter((w) => w.name || w.url),
    crew: { drivers: ids(d.drivers), helpers: ids(d.helpers), trucks: ids(d.trucks), trailers: ids(d.trailers) },
    tentative: d.tentative === true, // TASK-9750
    names: reply.names && typeof reply.names === 'object' ? reply.names : {},
    assumed,
    alreadyOnBoard: (Array.isArray(reply.alreadyOnBoard) ? reply.alreadyOnBoard : []).slice(0, 6).map((x) => str(x, 300)),
    approveByMs: Number.isFinite(reply.approveByMs) ? reply.approveByMs : null,
  };
}

/** Stable key for one assumed value (its confirm button and its amber mark). */
export function claudeAssumedKey(a) {
  return a && a.field && a.id ? `${a.field}:${a.id}` : null;
}

/**
 * The assumed values still waiting for a person. One counts as settled when it
 * was confirmed, or when the form no longer holds it (customer changed, that
 * crew member / unit unticked): the person changed it, which is a decision.
 * `form` = { customerId, selected: { drivers: [], helpers: [], trucks: [], trailers: [] } }.
 */
export function claudeDraftUnconfirmed(assumed, confirmed, form) {
  const done = confirmed instanceof Set ? confirmed : new Set(Array.isArray(confirmed) ? confirmed : []);
  const f = form || {};
  const sel = f.selected || {};
  return (Array.isArray(assumed) ? assumed : []).filter((a) => {
    const key = claudeAssumedKey(a);
    if (!key || done.has(key)) return false;
    // Claude named someone / something the form could not tick: only an
    // explicit "Leave it off" settles it.
    if (a.field === 'missing') return true;
    if (a.field === 'customer') return String(f.customerId || '') === a.id;
    return Array.isArray(sel[a.field]) && sel[a.field].includes(a.id);
  });
}

const FIELD_LABEL = { customer: 'Customer', drivers: 'Person', helpers: 'Helper', trucks: 'Vehicle', trailers: 'Trailer', missing: 'Not on this roster' };

/**
 * The banner at the top of the job form. Every value is escaped. Buttons carry
 * only the assumed key (field:id, ids are Firestore ids) in a data attribute.
 */
export function claudeDraftBannerHtml(facts, unconfirmed) {
  if (!facts) return '';
  const open = Array.isArray(unconfirmed) ? unconfirmed : [];
  const info = facts.assumed.filter((a) => !claudeAssumedKey(a));
  const rows = open.map((a) => `
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 8px;margin-top:6px;border:2px solid #F59E0B;border-radius:6px;background:#FFFBEB;">
        <span style="flex:1;min-width:180px;font-size:0.85rem;color:#0F172A;"><strong>${esc(FIELD_LABEL[a.field] || 'Value')}:</strong> ${esc(a.used)}${a.field === 'missing' ? ' <span style="color:#334155;">(Claude put this on the job; pick someone else or leave it off)</span>' : ` <span style="color:#334155;">(Claude was told "${esc(a.said)}")</span>`}</span>
        <button type="button" class="btn btn-secondary btn-small" data-cd-confirm="${esc(claudeAssumedKey(a))}" style="min-height:44px;">${a.field === 'missing' ? 'Leave it off' : 'Looks right'}</button>
      </div>`).join('');
  const infoRows = info.length
    ? `<div style="font-size:0.8rem;color:#334155;margin-top:6px;">Also noted: ${info.map((a) => esc(a.used)).join('; ')}</div>` : '';
  const board = facts.alreadyOnBoard.length
    ? `<div style="font-size:0.8rem;color:#92400E;margin-top:6px;"><strong>Already on the board when Claude asked:</strong> ${facts.alreadyOnBoard.map(esc).join('; ')}. It may be the same work.</div>` : '';
  const left = open.length
    ? `<div style="font-size:0.85rem;font-weight:600;color:#92400E;margin-top:8px;">${open.length} value${open.length === 1 ? '' : 's'} Claude guessed. Tap Looks right on each, or change it, before Save.</div>` : '';
  return `
    <div style="margin-bottom:var(--spacing-md, 16px);padding:10px 12px;border:1px solid #FCD34D;border-left:4px solid #F59E0B;border-radius:8px;background:#FFFDF5;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:600;font-size:0.875rem;color:#0F172A;">
        <span class="material-icons icon-dd-ai" aria-hidden="true" style="font-size:18px;color:#B45309;"></span>
        From Claude — not on the board yet
      </div>
      <div style="font-size:0.8rem;color:#334155;margin-top:4px;">
        Claude filled this in from a chat. Check every field, then Save. Nothing goes to the crew before you save. DOT flags and the price book were not set by Claude; a start time is only the one the request gave.
      </div>
      ${rows}${left}${infoRows}${board}
    </div>`;
}
